import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SectionTitle from "@/components/feature/SectionTitle";
import { reviews, siteInfo, positioning, milestones } from "@/mocks/site";
import { useSeo, SITE_NAME, SITE_ORIGIN } from "@/hooks/useSeo";

export default function About() {
  useSeo({
    title: "关于我们｜看料88 内容定位与编辑原则",
    description:
      "了解看料88的品牌定位、内容原则和发展历程。我们专注文娱热点的整理与时间线还原，坚持先核实再发布、围观有边界。",
    keywords: "关于看料88,看料88介绍,内容原则,文娱热点,编辑团队",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于看料88",
        url: `${SITE_ORIGIN}/about`,
        inLanguage: "zh-CN",
        description:
          "看料88 的品牌定位、内容原则与发展历程介绍，专注文娱热点的整理与时间线还原。",
        mainEntity: {
          "@type": "Organization",
          name: SITE_NAME,
          url: SITE_ORIGIN,
          email: siteInfo.email,
          foundingDate: siteInfo.established,
          description: siteInfo.intro,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE_ORIGIN}/about` },
        ],
      },
    ],
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8 md:py-14">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-12">
              <div className="lg:col-span-8">
                <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                  <i className="ri-information-line" />
                  关于我们
                </span>
                <h1 className="mt-4 font-heading text-3xl font-black leading-tight tracking-tight text-foreground-950 md:text-5xl">
                  关于看料88
                </h1>
                <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  {siteInfo.intro}
                </p>
              </div>
              <div className="lg:col-span-4">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-sm font-semibold text-foreground-950">快速了解</h2>
                  <ul className="mt-4 space-y-3 text-sm text-foreground-600">
                    <li className="flex items-start gap-2">
                      <i className="ri-calendar-line mt-0.5 text-primary-600" />
                      起步于 {siteInfo.established} 年
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-layout-grid-line mt-0.5 text-primary-600" />
                      六个内容栏目
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-mail-line mt-0.5 text-primary-600" />
                      {siteInfo.email}
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-time-line mt-0.5 text-primary-600" />
                      {siteInfo.serviceHours}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50" aria-labelledby="positioning-title">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-14 md:px-8 md:py-20">
            <SectionTitle
              eyebrow="网站定位"
              title={<span id="positioning-title">我们想做一件很具体的事</span>}
              description="把散落在各处的热门话题整理成看得懂的线索，让围观这件事变得更清楚一点。"
            />
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              {positioning.map((item) => (
                <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              ))}
            </div>

            <dl className="mt-10 grid grid-cols-2 gap-x-6 gap-y-6 border-t border-background-200 pt-8 md:grid-cols-4">
              {siteInfo.stats.map((stat) => (
                <div key={stat.label}>
                  <dd className="font-heading text-3xl font-black text-primary-600">{stat.value}</dd>
                  <dt className="mt-1 text-xs text-foreground-500">{stat.label}</dt>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section className="bg-background-100" aria-labelledby="about-principles-title">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-14 md:px-8 md:py-20">
            <SectionTitle
              eyebrow="内容原则"
              title={<span id="about-principles-title">红线、底线和取舍</span>}
              description="这些规则决定了什么内容会被收录，什么内容会被删掉。"
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:gap-5">
              {siteInfo.principles.map((item) => (
                <div
                  key={item.title}
                  className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-50" aria-labelledby="timeline-title">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-14 md:px-8 md:py-20">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
              <div className="lg:col-span-4">
                <SectionTitle
                  eyebrow="发展历程"
                  title={<span id="timeline-title">从一条话题到现在</span>}
                  description="没有爆发式的增长，只有一次次把规则写清楚的过程。"
                />
              </div>
              <div className="lg:col-span-8">
                <ol className="relative border-l border-background-300 pl-6">
                  {milestones.map((item) => (
                    <li key={item.year} className="relative pb-8 last:pb-0">
                      <span className="absolute -left-[31px] top-1 flex h-4 w-4 items-center justify-center rounded-full border-4 border-background-50 bg-primary-500" />
                      <span className="font-heading text-sm font-bold text-accent-700">{item.year}</span>
                      <h3 className="mt-1 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-foreground-950" aria-labelledby="reviews-title">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-14 md:px-8 md:py-20">
            <div className="mb-8">
              <span className="inline-flex items-center gap-2 rounded-full bg-background-800 px-3 py-1 text-xs font-medium text-accent-300">
                <i className="ri-chat-quote-line" />
                用户口碑
              </span>
              <h2
                id="reviews-title"
                className="mt-4 font-heading text-2xl font-bold text-background-50 md:text-3xl"
              >
                读者是怎么用我们的
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              {reviews.map((review) => (
                <figure key={review.name} className="rounded-lg border border-background-800 bg-background-900/60 p-6">
                  <div className="flex items-center gap-1 text-accent-400">
                    {[0, 1, 2, 3, 4].map((star) => (
                      <i key={star} className="ri-star-fill text-xs" />
                    ))}
                  </div>
                  <blockquote className="mt-4 text-sm leading-relaxed text-background-200">
                    “{review.text}”
                  </blockquote>
                  <figcaption className="mt-4 flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-bold text-background-50">
                      {review.name.slice(0, 1)}
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-background-50">{review.name}</span>
                      <span className="block text-xs text-background-400">{review.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-50" aria-labelledby="contact-title">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-14 md:px-8 md:py-20">
            <div className="grid grid-cols-1 gap-8 rounded-lg border border-background-200 bg-background-100 p-6 md:p-10 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-7">
                <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                  <i className="ri-customer-service-2-line" />
                  联系方式
                </span>
                <h2
                  id="contact-title"
                  className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl"
                >
                  有线索想提供，或者内容需要更正
                </h2>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
                  欢迎附上公开可查的来源链接发信给我们。核查后会及时更正或补充，采纳与否都会回复。
                </p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <a
                    href={`mailto:${siteInfo.email}`}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-mail-line" />
                    {siteInfo.email}
                  </a>
                  <Link
                    to="/materials"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-foreground-400"
                  >
                    浏览最新合集
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h3 className="font-heading text-sm font-semibold text-foreground-950">联系信息</h3>
                  <ul className="mt-4 space-y-3 text-sm text-foreground-600">
                    <li className="flex items-start gap-2">
                      <i className="ri-mail-line mt-0.5 text-primary-600" />
                      <a href={`mailto:${siteInfo.email}`} className="transition-colors hover:text-primary-600">
                        {siteInfo.email}
                      </a>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-time-line mt-0.5 text-primary-600" />
                      {siteInfo.serviceHours}
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-map-pin-line mt-0.5 text-primary-600" />
                      {siteInfo.baseCity}
                    </li>
                  </ul>
                  <p className="mt-4 border-t border-background-200 pt-4 text-xs leading-relaxed text-foreground-500">
                    本站内容均来自公开信息整理，不构成对任何个人或机构的评价。若相关内容涉及你的合法权益，请通过邮箱联系我们处理。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}